// Category IDs come from Product::$categories in the existing API.
export const COLLECTIONS = [
	{
		id: 'lego',
		category: 7000,
		name: 'LEGO',
		short: 'LEGO',
		types: ['sets', 'themes']
	},
	{
		id: 'magic',
		category: 1,
		name: 'Magic: The Gathering',
		short: 'Magic',
		types: ['singles', 'sealed', 'sets']
	},
	{
		id: 'pokemon',
		category: 3,
		name: 'Pokémon',
		short: 'Pokémon',
		types: ['singles', 'sealed', 'sets']
	},
	{
		id: 'yugioh',
		category: 2,
		name: 'Yu-Gi-Oh!',
		short: 'Yu-Gi-Oh!',
		types: ['singles', 'sealed', 'sets']
	},
	{
		id: 'flesh-and-blood',
		category: 62,
		name: 'Flesh & Blood',
		short: 'Flesh & Blood',
		types: ['singles', 'sealed', 'sets']
	},
	{
		id: 'force-of-will',
		category: 17,
		name: 'Force of Will',
		short: 'Force of Will',
		types: ['singles', 'sealed', 'sets']
	},
	{
		id: 'lorcana',
		category: 71,
		name: 'Lorcana',
		short: 'Lorcana',
		types: ['singles', 'sealed', 'sets']
	},
	{
		id: 'digimon',
		category: 63,
		name: 'Digimon',
		short: 'Digimon',
		types: ['singles', 'sealed', 'sets']
	},
	{
		id: 'star-wars-destiny',
		category: 26,
		name: 'Star Wars: Destiny',
		short: 'Star Wars: Destiny',
		types: ['singles', 'sealed', 'sets']
	},
	{
		id: 'star-wars-unlimited',
		category: 79,
		name: 'Star Wars: Unlimited',
		short: 'Star Wars: Unlimited',
		types: ['singles', 'sealed', 'sets']
	},
	{
		id: 'one-piece',
		category: 68,
		name: 'One Piece',
		short: 'One Piece',
		types: ['singles', 'sealed', 'sets']
	},
	{
		id: 'riftbound',
		category: 89,
		name: 'Riftbound',
		short: 'Riftbound',
		types: ['singles', 'sealed', 'sets']
	}
];
// Exact legacy theme names, used only until the API supplies category metadata.
const LEGO_THEMES = new Set([
	'Architecture',
	'Art',
	'BrickHeadz',
	'City',
	'Classic',
	'Creator',
	'Creator Expert',
	'DC Comics Super Heroes',
	'Disney',
	'DOTS',
	'DUPLO',
	'Friends',
	'Ghostbusters',
	'Harry Potter',
	'Icons',
	'Ideas',
	'Jurassic World',
	'LEGO Ideas',
	'Marvel Super Heroes',
	'Minecraft',
	'Ninjago',
	'Speed Champions',
	'Star Wars',
	'Technic',
	'The Lord of the Rings',
	'The Simpsons'
]);
export function buildCatalog(data) {
	const rows = Array.isArray(data) ? data : Object.values(data || {});
	return new Map(
		rows
			.filter(row => row && row.id)
			.map(row => [
				String(row.id),
				{ ...row, tcg_category: row.tcg_category || 1 }
			])
	);
}
export function collectionFor(set, catalog) {
	const entry = catalog.get(String(set.set_id));
	const category = Number(set.tcg_category || (entry && entry.tcg_category));
	const explicit = COLLECTIONS.find(item => item.category === category);
	if (category) return explicit ? explicit.id : 'other';
	if (LEGO_THEMES.has(set.set_name)) return 'lego';
	return 'other';
}
export function typeFor(name, set, collection) {
	if (set.watch_scope === 'set')
		return collection === 'lego' ? 'themes' : 'sets';
	if (collection === 'lego') return 'sets';
	if (set.product_type === 'sealed' || set.product_type === 'singles')
		return set.product_type;
	if (set.number || set.type) return 'singles';
	// The legacy importer uses these product names/metadata for sealed goods.
	return /(?:booster (?:box|pack|display)|elite trainer|starter deck|theme deck|deck display|\bdeck\b|display box|fat pack|^bundle$|^box set$|^.* tin$|^.* blister$|^secret lair)/i.test(
		name
	) ||
		/(?:boosters|sealed|case display|collectors boosters)/i.test(
			set.tcgplayer_meta || set.set_name || ''
		)
		? 'sealed'
		: 'singles';
}
export function groupWatches(rows, catalog) {
	return (Array.isArray(rows) ? rows : []).flatMap(row => {
		const groups = {};
		(Array.isArray(row.sets) ? row.sets : []).forEach(set => {
			const collection = collectionFor(set, catalog);
			const type = typeFor(row.card_name, set, collection);
			// Sealed watches display each physical product separately while retaining
			// the shared card_name used by the saved watch/API.
			const key =
				collection +
				':' +
				type +
				(type === 'sealed' ? ':' + set.card_id : '');
			if (!groups[key])
				groups[key] = {
					card_name: row.card_name,
					...(type === 'sealed' && set.set_name
						? { display_name: `${set.set_name} — ${row.card_name}` }
						: {}),
					collection,
					type,
					sets: []
				};
			groups[key].sets.push(set);
		});
		// Keep legacy watches without variants visible and editable.
		return Object.keys(groups).length
			? Object.values(groups)
			: [{ ...row, sets: [], collection: 'other', type: 'singles' }];
	});
}
export function scopedMatches(data, catalog, collection, type) {
	return Object.entries(data || {})
		.filter(([, sets]) => Array.isArray(sets))
		.map(([name, sets]) => ({
			card_name: name,
			collection,
			type,
			sets: sets.filter(
				set =>
					collectionFor(set, catalog) === collection &&
					typeFor(name, set, collection) === type
			)
		}))
		.filter(item => item.sets.length);
}
export function imageFor(product) {
	if (product.sets && product.sets.some(set => set.watch_scope === 'set'))
		return null;
	const set = product.sets && product.sets[0];
	if (!set) return null;
	if (set.image_url && /^https:\/\//.test(set.image_url))
		return set.image_url;
	if (set.tcgplayer_id)
		return `https://product-images.tcgplayer.com/fit-in/400x558/${encodeURIComponent(
			set.tcgplayer_id
		)}.jpg`;
	if (product.collection === 'lego' && set.number) {
		const number = String(set.number).replace(/-\d+$/, '') + '-1';
		return `https://img.bricklink.com/ItemImage/SN/0/${encodeURIComponent(
			number
		)}.png`;
	}
	if (product.collection === 'magic' && product.type === 'singles') {
		return `https://api.scryfall.com/cards/named?exact=${encodeURIComponent(
			product.card_name
		)}&format=image&version=small`;
	}
	return null;
}
export function isSetWatch(collection, type) {
	return collection === 'lego' ? type === 'themes' : type === 'sets';
}
export function setMatches(catalog, collection, term) {
	const current = COLLECTIONS.find(item => item.id === collection);
	if (!current) return [];
	return Array.from(catalog.values())
		.filter(
			set =>
				Number(set.tcg_category) === current.category &&
				set.name.toLowerCase().includes(term.trim().toLowerCase())
		)
		.map(set => ({
			card_name: `${current.short} ${
				collection === 'lego' ? 'theme' : 'set'
			}: ${set.name}`,
			collection,
			type: collection === 'lego' ? 'themes' : 'sets',
			sets: [
				{
					card_id: `set:${set.id}`,
					set_id: set.id,
					set_name: set.name,
					tcg_category: set.tcg_category,
					watch_scope: 'set'
				}
			]
		}));
}
export function mergeWatchSets(rows, name, replacement, removed = []) {
	const existing = rows.find(row => row.card_name === name);
	const removing = new Set(removed.map(set => String(set.card_id)));
	const sets = new Map(
		(existing ? existing.sets : [])
			.filter(set => !removing.has(String(set.card_id)))
			.map(set => [String(set.card_id), set])
	);
	replacement.forEach(set => sets.set(String(set.card_id), set));
	return Array.from(sets.values());
}
